import React, { useState, useEffect } from 'react';
import { Save, Plus, Trash2, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Separator } from '../../components/ui/separator';
import { adminGetSiteSettings, adminUpdateSiteSettings } from '../../lib/api';
import { toast } from 'sonner';

export default function SettingsTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [contact, setContact] = useState({ email: '', phone: '' });
  const [ecosystem, setEcosystem] = useState([]);
  const [social, setSocial] = useState([]);

  useEffect(() => {
    adminGetSiteSettings()
      .then(res => {
        const d = res.data;
        setContact(d.contact || { email: '', phone: '' });
        setEcosystem(d.ecosystem || []);
        setSocial(d.social || []);
      })
      .catch(() => toast.error('Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminUpdateSiteSettings({ contact, ecosystem, social });
      toast.success('Footer settings saved');
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addEcosystemItem = () => setEcosystem([...ecosystem, { name: '', url: '' }]);
  const removeEcosystemItem = (i) => setEcosystem(ecosystem.filter((_, idx) => idx !== i));
  const updateEcosystem = (i, field, value) => {
    const updated = [...ecosystem];
    updated[i] = { ...updated[i], [field]: value };
    setEcosystem(updated);
  };

  const addSocialItem = () => setSocial([...social, { platform: '', url: '', handle: '' }]);
  const removeSocialItem = (i) => setSocial(social.filter((_, idx) => idx !== i));
  const updateSocial = (i, field, value) => {
    const updated = [...social];
    updated[i] = { ...updated[i], [field]: value };
    setSocial(updated);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-5 w-5 animate-spin text-white/30" />
      </div>
    );
  }

  return (
    <div data-testid="settings-tab">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold tracking-[-0.02em]">Footer Settings</h1>
        <Button onClick={handleSave} disabled={saving} className="bg-[#FF6B00] text-white hover:bg-[#FF6B00]/90 font-semibold" data-testid="settings-save-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="space-y-8">
        {/* Contact Section */}
        <section className="rounded-xl border border-white/8 bg-white/[0.02] p-5">
          <h3 className="text-sm font-semibold text-white/80 mb-4">Contact Info</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-white/55 mb-1.5 block">Email</label>
              <Input
                value={contact.email}
                onChange={e => setContact({ ...contact, email: e.target.value })}
                placeholder="info@visualte.com"
                className="bg-black/40 border-white/10 text-white"
                data-testid="settings-contact-email"
              />
            </div>
            <div>
              <label className="text-xs text-white/55 mb-1.5 block">Phone</label>
              <Input
                value={contact.phone}
                onChange={e => setContact({ ...contact, phone: e.target.value })}
                placeholder="+972 50 237 2931"
                className="bg-black/40 border-white/10 text-white"
                data-testid="settings-contact-phone"
              />
            </div>
          </div>
        </section>

        <Separator className="bg-white/8" />

        {/* Ecosystem Section */}
        <section className="rounded-xl border border-white/8 bg-white/[0.02] p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-white/80">Ecosystem Links</h3>
            <Button variant="ghost" size="sm" onClick={addEcosystemItem} className="text-white/50 hover:text-white text-xs" data-testid="settings-add-ecosystem">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add Link
            </Button>
          </div>
          <div className="space-y-3">
            {ecosystem.map((item, i) => (
              <div key={i} className="flex items-center gap-3" data-testid={`settings-ecosystem-${i}`}>
                <Input
                  value={item.name}
                  onChange={e => updateEcosystem(i, 'name', e.target.value)}
                  placeholder="Name (e.g. Onlenu)"
                  className="bg-black/40 border-white/10 text-white flex-1"
                />
                <Input
                  value={item.url}
                  onChange={e => updateEcosystem(i, 'url', e.target.value)}
                  placeholder="URL (e.g. /powered/onlenu)"
                  className="bg-black/40 border-white/10 text-white flex-1 font-mono text-sm"
                />
                <Button variant="ghost" size="sm" onClick={() => removeEcosystemItem(i)} className="text-red-400/60 hover:text-red-400 flex-shrink-0">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {ecosystem.length === 0 && <p className="text-xs text-white/30 py-2">No ecosystem links added.</p>}
          </div>
        </section>

        <Separator className="bg-white/8" />

        {/* Social Links Section */}
        <section className="rounded-xl border border-white/8 bg-white/[0.02] p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-white/80">Social Links</h3>
            <Button variant="ghost" size="sm" onClick={addSocialItem} className="text-white/50 hover:text-white text-xs" data-testid="settings-add-social">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add Social
            </Button>
          </div>
          <div className="space-y-3">
            {social.map((item, i) => (
              <div key={i} className="flex items-center gap-3 flex-wrap sm:flex-nowrap" data-testid={`settings-social-${i}`}>
                <Input
                  value={item.platform}
                  onChange={e => updateSocial(i, 'platform', e.target.value)}
                  placeholder="Platform (e.g. Instagram)"
                  className="bg-black/40 border-white/10 text-white w-full sm:w-auto sm:flex-1"
                />
                <Input
                  value={item.url}
                  onChange={e => updateSocial(i, 'url', e.target.value)}
                  placeholder="URL (e.g. https://instagram.com/...)"
                  className="bg-black/40 border-white/10 text-white w-full sm:w-auto sm:flex-1 font-mono text-sm"
                />
                <Input
                  value={item.handle || ''}
                  onChange={e => updateSocial(i, 'handle', e.target.value)}
                  placeholder="Handle (e.g. @visualte.co)"
                  className="bg-black/40 border-white/10 text-white w-full sm:w-auto sm:flex-1 font-mono text-sm"
                />
                <Button variant="ghost" size="sm" onClick={() => removeSocialItem(i)} className="text-red-400/60 hover:text-red-400 flex-shrink-0">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {social.length === 0 && <p className="text-xs text-white/30 py-2">No social links added.</p>}
          </div>
        </section>
      </div>
    </div>
  );
}
